import { addRollupValues, initRollupAcc } from "utils/metrics";
import {
  LOSS_CATEGORIES_STRINGS,
  PROFIT_CATEGORIES_STRINGS,
} from "constants/constants";
import { TRENDS_BIN_SIZE_VALUES_MS_MAP } from "utils/timeUtils";
import { camelCase, cloneDeep } from "lodash";
import { mapPnlCategory } from "utils/pnlMapping";
import type {
  RollupWithTagsAndCountsAndRange,
  TimeRangeFinite,
  ManyRollupsWithTagsArrays,
  TrendsPageFilterStateType,
  RollupWithTagsAndCounts,
} from "types/types";
import { PnlCategoriesEnum, TrendChartBinSizesEnum } from "types/enums";
import { parseTrendsPageFilterState } from "../../FlightTrends/trendsPageUtils";
import { useRef } from "react";
import { NO_SHIFT_SCHEDULED } from "../range-selectors/steppable-range-selectors/ShiftSelector/shiftUtils";
import { NOT_SCHEDULED_TITLE } from "../PnlSummaryChart/pnlSummaryChartUtils";

export const highlightTimelineSegmentsInRange = ({
  category,
  doHighlight,
  range,
}) => {
  const pnlSegments = document.querySelectorAll<HTMLElement>(
    `.production-segment-${camelCase(mapPnlCategory(category))}`
  );

  if (pnlSegments) {
    pnlSegments.forEach((segment: HTMLElement) => {
      const segmentRange =
        segment &&
        (segment.dataset
          ? {
              ge: segment.dataset.rangeGe,
              lt: segment.dataset.rangeLt,
            }
          : {
              // no dataset on IE11
              ge: segment.getAttribute("data-rangeGe"),
              lt: segment.getAttribute("data-rangeLt"),
            });
      if (
        segment &&
        segmentRange &&
        segmentRange.ge &&
        // if the segment touches the range at all:
        // the segment is completely inside the range
        ((segmentRange.lt &&
          segmentRange.ge >= range.ge &&
          segmentRange.lt < range.lt) ||
          // or the segment open-ended, and its ge is inside the range
          (!segmentRange.lt && segmentRange.ge < range.lt) ||
          // or the segment's lt is inside the range
          (segmentRange.lt &&
            segmentRange.lt > range.ge &&
            segmentRange.lt <= range.lt) ||
          // or the segment's lt is above the range & ge is below the range
          (segmentRange.lt &&
            segmentRange.lt > range.lt &&
            segmentRange.ge <= range.ge))
      ) {
        segment.style.opacity = doHighlight ? "0.5" : "1";
      }
    });
  }
};

export const trendTooltipHighlight = ({ category, rollupMetric }) =>
  highlightTimelineSegmentsInRange({
    category,
    range: rollupMetric.range,
    doHighlight: true,
  });
export const trendTooltipUnhighlight = ({ category, rollupMetric }) =>
  highlightTimelineSegmentsInRange({
    category,
    range: rollupMetric.range,
    doHighlight: false,
  });

export const getIsCurrent = ({
  now,
  rangeGe,
  rangeLt,
}: {
  now: number;
  rangeGe: number;
  rangeLt: number;
}) => Number(rangeGe) <= now && now < Number(rangeLt);

type getTrendChartNegativeMarginBottomPctProps = {
  rollupMetricTrendChartBarsArray: RollupWithTagsAndCountsAndRange[];
  shouldShrinkBottomHalfToFit?: boolean;
  numProcesses: number;
};
export const getTrendChartNegativeMarginBottomPct = ({
  rollupMetricTrendChartBarsArray,
  shouldShrinkBottomHalfToFit = true,
  numProcesses,
}: getTrendChartNegativeMarginBottomPctProps) => {
  const now = Date.now();

  // chart starts a bit shorter, then grows down if bottom wings need room to grow
  const biggestLossBarstackPercent = rollupMetricTrendChartBarsArray.reduce(
    (acc, rollupMetric) => {
      const rangeGe = rollupMetric.range.ge;
      const isCurrentBar = getIsCurrent({
        now,
        rangeGe,
        rangeLt: rollupMetric.range.lt,
      });
      // expand the current bar to match other bar heights
      const rangeLt = isCurrentBar ? now : rollupMetric.range.lt;
      const barRange = rangeLt - rangeGe;

      return Math.max(
        acc,
        LOSS_CATEGORIES_STRINGS.reduce((lossAcc, category) => {
          // how much height does each bar contribute to the barstack height?
          const timeInCategory = rollupMetric[category];
          const totalTime = numProcesses * barRange;

          const rollupHeightPct =
            totalTime === 0 ? 0 : timeInCategory / totalTime;

          return lossAcc + Math.max(rollupHeightPct, 0);
        }, 0)
      );
    },
    0
  );

  // pull up the bottom of the chart until we hit the bottom of the biggest loss barstack
  return !shouldShrinkBottomHalfToFit
    ? 0
    : 1 - (Math.max(0, Math.min(biggestLossBarstackPercent, 1)) || 0); // getting some slight overlap sometimes, not sure why 😩
};

export type BiggestDurationRollupType = {
  rollupMetric: RollupWithTagsAndCountsAndRange | null;
  duration: number;
};
type getBiggestDurationTrendChartBarRollupProps = {
  rollupMetricTrendChartBarsArray: RollupWithTagsAndCountsAndRange[];
  shouldHideProfitCategories: boolean;
  shouldExpandCurrentBarHeightToMatchOthers?: boolean;
  shouldUseDataDumpsForTrendChartBarstacks: boolean;
  extraLossCategories: string[];
};
export const getBiggestDurationTrendChartBarRollup = ({
  rollupMetricTrendChartBarsArray,
  shouldHideProfitCategories,
  shouldExpandCurrentBarHeightToMatchOthers = false,
  shouldUseDataDumpsForTrendChartBarstacks,
  extraLossCategories = [],
}: getBiggestDurationTrendChartBarRollupProps): BiggestDurationRollupType | null => {
  const showPlannedAsLoss = extraLossCategories.includes(
    PnlCategoriesEnum.planned
  );
  const showNotScheduledAsLoss = extraLossCategories.includes(
    PnlCategoriesEnum.notScheduled
  );
  const showQualityLoss = shouldUseDataDumpsForTrendChartBarstacks;
  return !rollupMetricTrendChartBarsArray
    ? null
    : rollupMetricTrendChartBarsArray.reduce(
        (biggest, rollupMetric) => {
          const { totalProfit, totalLoss } = Object.entries(
            rollupMetric
          ).reduce(
            (acc, [category, time]) => {
              const now = Date.now();
              const isCurrentBar = getIsCurrent({
                now,
                rangeGe: rollupMetric.range.ge,
                rangeLt: rollupMetric.range.lt,
              });
              const rollupRange = rollupMetric.range.lt - rollupMetric.range.ge;
              const barRange = isCurrentBar
                ? now - rollupMetric.range.ge
                : rollupRange;
              // expand the current bar height to match the others
              // unless we're only showing loss categories
              const currentBarMultiplier =
                shouldExpandCurrentBarHeightToMatchOthers
                  ? rollupRange / barRange
                  : 1;
              if (
                !shouldHideProfitCategories &&
                PROFIT_CATEGORIES_STRINGS.includes(category)
              ) {
                acc.totalProfit += (time as number) * currentBarMultiplier;
              } else if (
                [
                  ...LOSS_CATEGORIES_STRINGS,
                  ...(showQualityLoss ? ["qualityLoss"] : []),
                  ...(showPlannedAsLoss ? ["planned"] : []),
                  ...(showNotScheduledAsLoss ? ["notScheduled"] : []),
                ].includes(category)
              ) {
                acc.totalLoss += (time as number) * currentBarMultiplier;
              }
              return acc;
            },
            { totalProfit: 0, totalLoss: 0 }
          );
          if (totalProfit > biggest.duration) {
            biggest.duration = totalProfit;
            biggest.rollupMetric = rollupMetric;
          }
          if (totalLoss > biggest.duration) {
            biggest.duration = totalLoss;
            biggest.rollupMetric = rollupMetric;
          }
          return biggest;
        },
        {
          rollupMetric: null as RollupWithTagsAndCountsAndRange | null,
          duration: 0,
        }
      );
};

const makeEmptyTrendChartBarRollup = ({
  focusRange,
  numTrendChartBars,
  idx,
}: {
  focusRange: TimeRangeFinite;
  numTrendChartBars: number;
  idx: number;
}) => {
  const focusRangeDuration = focusRange.lt - focusRange.ge;
  const barDuration = focusRangeDuration / numTrendChartBars;
  const thisBarGe = focusRange.ge + idx * barDuration;
  return {
    ...initRollupAcc(),
    range: {
      ge: thisBarGe,
      lt: Math.min(thisBarGe + barDuration, focusRange.lt),
    },
  };
};

const addRollupsArrays = (
  rollupsMetricArray: ManyRollupsWithTagsArrays,
  visibleProcessIds?: string[]
): RollupWithTagsAndCountsAndRange[] => {
  // ManyRollupsWithTagsArrays = {
  //   [key: string]: RollupWithTagsAndCounts[];
  // }
  // split rollupsMetricArray into array of ManyRollupsWithTags
  const rollupsEntries =
    rollupsMetricArray && Object.entries(rollupsMetricArray);
  return !rollupsEntries
    ? ([] as RollupWithTagsAndCountsAndRange[])
    : rollupsEntries.reduce((acc, [processId, rollupsArray]) => {
        if (!visibleProcessIds || visibleProcessIds.includes(processId)) {
          // add each rollup together by its index
          rollupsArray.forEach((rollup, idx) => {
            // add it to the previous accumulator value at that index
            const rangeDurationForRollup = rollup.range.lt - rollup.range.ge;
            acc[idx] = !acc[idx]
              ? cloneDeep(rollup)
              : addRollupValues(
                  [acc[idx], cloneDeep(rollup)],
                  rangeDurationForRollup
                );
          });
        }
        return acc;
      }, [] as RollupWithTagsAndCountsAndRange[]);
};
export const addTrendChartBarsArrays = ({
  allRollupsMetricTrendChartBarsArrays,
  trendChartBinSize,
  visibleProcessIds,
  focusRange,
}: {
  allRollupsMetricTrendChartBarsArrays: ManyRollupsWithTagsArrays;
  trendChartBinSize: TrendChartBinSizesEnum;
  visibleProcessIds: string[];
  focusRange: TimeRangeFinite | null;
}): RollupWithTagsAndCountsAndRange[] => {
  if (!focusRange) {
    return [];
  }

  const rollupMetricTrendChartBarsArray = addRollupsArrays(
    allRollupsMetricTrendChartBarsArrays,
    visibleProcessIds
  );

  const focusRangeDuration = focusRange ? focusRange.lt - focusRange.ge : 0;
  const barsInFocusRange =
    focusRangeDuration / TRENDS_BIN_SIZE_VALUES_MS_MAP[trendChartBinSize];

  const numTrendChartBars = Math.max(
    barsInFocusRange,
    rollupMetricTrendChartBarsArray.length
  );

  const arrayLength = Math.max(0, Math.round(numTrendChartBars)) || 0; // catch any NaN;
  return [...Array(arrayLength)].map(
    (_, idx) =>
      rollupMetricTrendChartBarsArray[idx] ||
      makeEmptyTrendChartBarRollup({ focusRange, numTrendChartBars, idx })
  );
};

// empty bar data except for "range"
export const EMPTY_TREND_BAR = {
  counts: {
    producing: {},
    planned: {},
    unplanned: {},
    waiting: {},
    unknown: {},
    notScheduled: {},
    uncollected: {},
  },
  notScheduled: 0,
  planned: 0,
  producing: 0,
  totalDurationMs: 0,
  uncollected: 0,
  unknown: 0,
  unplanned: 0,
  waiting: 0,
  tags: {
    notScheduled: {},
    planned: {},
    producing: {},
    uncollected: {},
    unknown: {},
    unplanned: {},
    waiting: {},
  },
} as RollupWithTagsAndCounts;

const FILTER_TAGS_REPRESENTING_CATEGORIES = [
  "producing",
  "uncollected",
  "unknown",
];

/** return true if the range is found within any of the ranges
 */
function getIsRangeWithinRanges(
  range: TimeRangeFinite,
  ranges: TimeRangeFinite[]
): boolean {
  return ranges.reduce((acc, currentRange) => {
    if (range.ge >= currentRange.ge && range.ge < currentRange.lt) {
      acc = true;
    }
    return acc;
  }, false as boolean);
}

// tslint:disable-next-line: cognitive-complexity
export function filterTrendChartBarsForTrendsPage({
  trendChartBarsArrayFilteredByProcess,
  trendsPageFilterState,
  trendChartBinSize,
  focusRange,
  shiftRanges,
}: {
  trendChartBarsArrayFilteredByProcess: RollupWithTagsAndCountsAndRange[];
  trendsPageFilterState: TrendsPageFilterStateType;
  trendChartBinSize: TrendChartBinSizesEnum;
  focusRange: TimeRangeFinite | null;
  shiftRanges: TimeRangeFinite[] | null;
}): RollupWithTagsAndCountsAndRange[] {
  const { tag, shiftName, pnlCategory } = trendsPageFilterState;
  const {
    isTrendsPageFilteredByTag,
    isTrendsPageFilteredByPnlCategory,
    isTrendsPageFilteredByShift,
  } = parseTrendsPageFilterState({ trendsPageFilterState });

  const duration = focusRange ? focusRange.lt - focusRange.ge : 0;
  // TODO verify that this is correct
  const isTrendsPage24HourMode = duration <= TRENDS_BIN_SIZE_VALUES_MS_MAP.days;

  // filter by shift *ranges* only in 24-hour + filter-by-shift mode
  // (24-hour mode intersects with the shift ranges so we pass those ranges and ask for aggregates;
  // other ranges are long enough to filter by shift on Askr side)
  const shouldFilterByShiftRange =
    isTrendsPage24HourMode && isTrendsPageFilteredByShift;

  const waitingForShiftAggregates =
    !trendChartBarsArrayFilteredByProcess[0].shift;

  const waitingToFilterByShift =
    isTrendsPageFilteredByShift && waitingForShiftAggregates;

  const shouldFilterByShift =
    isTrendsPageFilteredByShift &&
    // wait for AggregatesGetter to fetch the shiftly rollups
    !waitingForShiftAggregates &&
    // in 24 hour mode, we filter the bars to match the shift range instead
    !shouldFilterByShiftRange;

  const notFiltering =
    !isTrendsPageFilteredByTag &&
    !isTrendsPageFilteredByPnlCategory &&
    !shouldFilterByShift && // ensure we're not filtered by shift,
    !waitingToFilterByShift && // or waiting to filter by shifts
    !shouldFilterByShiftRange;

  if (notFiltering || !focusRange) {
    // don't filter by anything
    return trendChartBarsArrayFilteredByProcess;
  } else {
    let trendChartBarsDisplay = [] as RollupWithTagsAndCountsAndRange[];

    if (shouldFilterByShiftRange) {
      // filter by shift if necessary
      const trendChartBarsFilteredByShift =
        trendChartBarsArrayFilteredByProcess.filter(
          ({ range }) =>
            // filter by shift range
            // if shift overlaps with focus range,
            // find all bars which match the shift name
            shiftRanges && getIsRangeWithinRanges(range, shiftRanges)
        );

      trendChartBarsDisplay = trendChartBarsFilteredByShift.filter(rollup =>
        Boolean(rollup)
      );
      // if we're filtering by shift ranges, we need to sum the shiftly aggregates
      trendChartBarsDisplay =
        shiftRanges?.reduce((acc, shiftRange) => {
          const rollupsWithinRange = trendChartBarsDisplay.filter(
            ({ range }) =>
              range.ge >= shiftRange.ge && range.lt <= shiftRange.lt
          );
          // add together shiftly aggregates
          const shiftRollup = addRollupValues(rollupsWithinRange);
          return [...acc, shiftRollup];
        }, [] as RollupWithTagsAndCountsAndRange[]) ?? [];
    } else if (shouldFilterByShift) {
      const trendChartBarsFilteredByShift = // add together shiftly aggregates
        addShiftlyRollups({
          rollupsArr: trendChartBarsArrayFilteredByProcess.filter(
            ({ shift }) =>
              // filter by shift name
              shift &&
              (shift.name === shiftName ||
                // some shifts are internally named "Shift Not Specified" but displayed as "Not Scheduled"
                (shift.name === NO_SHIFT_SCHEDULED &&
                  shiftName === NOT_SCHEDULED_TITLE))
          ),
          trendChartBinSize,
          shiftName,
          focusRange,
        });
      trendChartBarsDisplay = trendChartBarsFilteredByShift.filter(rollup =>
        Boolean(rollup)
      );
    } else {
      trendChartBarsDisplay = trendChartBarsArrayFilteredByProcess;
    }

    // if we're filtering by tag,
    // sum the tag times for each pnl category
    if (isTrendsPageFilteredByTag) {
      const { totalTagTime, barsSummedByTags } = getBarsSummedByTags({
        tag,
        pnlCategoryOfTag: pnlCategory,
        rollupMetricTrendChartBarsArray: trendChartBarsDisplay,
      });

      // when a "tag" is actually a category:
      // for filter tags like "producing" and "unkown", there aren't any tags (usually)
      // if there's no tag, we might be looking for an entire category instead
      const lowercaseTag = tag.toLowerCase();
      // TECHDEBT: check what happens with producing tags
      const isTagAPnlCategory =
        isTrendsPageFilteredByPnlCategory &&
        FILTER_TAGS_REPRESENTING_CATEGORIES.includes(lowercaseTag) &&
        totalTagTime === 0;
      if (isTagAPnlCategory) {
        trendChartBarsDisplay = barsSummedByTags.map((bar, idx) => {
          // pull the original duration and percent from the original bar
          const totalDurationOfCategory =
            trendChartBarsDisplay[idx][lowercaseTag];
          return {
            ...bar,
            // e.g. "producing"
            [lowercaseTag]: totalDurationOfCategory,
            totalDurationMs: totalDurationOfCategory,
          };
        });
      } else {
        trendChartBarsDisplay = barsSummedByTags;
      }
      // if we're only filtering by a category, not a tag, and not a tag that represents a whole category
    } else if (isTrendsPageFilteredByPnlCategory && pnlCategory) {
      // take only the pnl category of each bar
      const barsByPnlCategoryDurations = getBarsByPnlCategoryDurations({
        pnlCategoryToKeep: pnlCategory,
        rollupMetricTrendChartBarsArray: trendChartBarsDisplay,
      });

      trendChartBarsDisplay = barsByPnlCategoryDurations;
    }

    return trendChartBarsDisplay;
  }
}

function getBarsByPnlCategoryDurations({
  pnlCategoryToKeep,
  rollupMetricTrendChartBarsArray,
}: {
  pnlCategoryToKeep: string;
  rollupMetricTrendChartBarsArray: RollupWithTagsAndCountsAndRange[];
}): RollupWithTagsAndCountsAndRange[] {
  return rollupMetricTrendChartBarsArray.map(
    (trendChartBar: RollupWithTagsAndCountsAndRange) => {
      // find the tag in the "tags" object
      const pnlCategoryTime: number | undefined =
        trendChartBar[pnlCategoryToKeep];

      return {
        ...EMPTY_TREND_BAR,
        range: { ...trendChartBar.range },
        [pnlCategoryToKeep]: pnlCategoryTime || 0,
        totalDurationMs: pnlCategoryTime || 0,
      } as RollupWithTagsAndCountsAndRange;
    }
  );
}

function getBarsSummedByTags({
  tag,
  pnlCategoryOfTag,
  rollupMetricTrendChartBarsArray,
}: {
  tag: string;
  pnlCategoryOfTag: string;
  rollupMetricTrendChartBarsArray: RollupWithTagsAndCountsAndRange[];
}): {
  totalTagTime: number;
  barsSummedByTags: RollupWithTagsAndCountsAndRange[];
} {
  let totalTagTime = 0;
  const barsSummedByTags = rollupMetricTrendChartBarsArray.map(
    (trendChartBar: RollupWithTagsAndCountsAndRange) => {
      // find the tag in the "tags" object
      const { pnlCategory, tagTime } = Object.entries(
        trendChartBar.tags
      ).reduce(
        (acc, [category, tagsObject]) => {
          const foundTagEntry = Object.entries(tagsObject).find(([name]) => {
            return camelCase(name) === camelCase(tag);
          });
          // if we found one, add it to the total
          if (foundTagEntry) {
            const [tagName, thisTagTime] = foundTagEntry;
            if (camelCase(tagName) === camelCase(tag)) {
              acc.pnlCategory = category;
              acc.tagTime = thisTagTime;
              totalTagTime += thisTagTime;
            }
          }
          return acc;
        },
        { pnlCategory: null as string | null, tagTime: null as number | null }
      );

      return {
        ...EMPTY_TREND_BAR,
        range: { ...trendChartBar.range },
        ...(pnlCategory ? { [pnlCategory]: tagTime } : {}),
        tags: {
          ...EMPTY_TREND_BAR.tags,
          [pnlCategoryOfTag]: { [tag]: tagTime },
        },
        totalDurationMs: tagTime || 0,
      } as RollupWithTagsAndCountsAndRange;
    }
  );
  return { totalTagTime, barsSummedByTags };
}

// combine an array of shiftly rollups into trend chart bins
export function addShiftlyRollups({
  rollupsArr,
  trendChartBinSize = null,
  shiftName,
  focusRange,
}: {
  rollupsArr: RollupWithTagsAndCountsAndRange[];
  trendChartBinSize?: TrendChartBinSizesEnum | null;
  shiftName: string;
  focusRange: TimeRangeFinite | null;
}): RollupWithTagsAndCountsAndRange[] {
  if (!focusRange) {
    return [];
  }

  const focusRangeDuration = focusRange.lt - focusRange.ge;
  // combine the rollups into the trend chart bars
  const binDuration = trendChartBinSize
    ? TRENDS_BIN_SIZE_VALUES_MS_MAP[trendChartBinSize]
    : // if no bin size passed in, we'll return a single rollup
      focusRangeDuration;

  const numBins = Math.ceil(focusRangeDuration / binDuration);
  const binEndTimesArr = [...new Array(numBins)].map(
    (_, idx) => focusRange.ge + (idx + 1) * binDuration
  );
  // split the rollups into binned arrays, one bin per trend chart bar
  const binnedRollupsArrays = rollupsArr.reduce((acc, rollup) => {
    // combine shifts by their start times
    const binIndex = binEndTimesArr.findIndex(
      // find the first bin whose end time is greater than the rollup start time
      binEndTime => binEndTime > rollup.range.ge
    );
    acc[binIndex] = acc[binIndex] ? [...acc[binIndex], rollup] : [rollup];
    return acc;
  }, [] as RollupWithTagsAndCountsAndRange[][]);

  // roll up the binned rollups into an array of single rollups!
  return binnedRollupsArrays.map(binnedRollupArray => ({
    ...addRollupValues(binnedRollupArray, binDuration),
    shift: { name: shiftName },
  }));
}

// for pnl and butterfly we want to return a single rollup over the entire range
export function addShiftlyRollupsIntoSingleRollup({
  rollupsArr,
  shiftName,
  focusRange,
}: {
  rollupsArr: RollupWithTagsAndCountsAndRange[];
  shiftName: string;
  focusRange: TimeRangeFinite | null;
}): RollupWithTagsAndCountsAndRange {
  return addShiftlyRollups({ rollupsArr, shiftName, focusRange })[0];
}

export function isEmptyObject(object: Record<string, unknown>) {
  return Object.values(object).length === 0;
}

/** drag handlers to allow the trend chart to be dragged left and right with the mouse
 */
export function useTrendChartDragHandlers() {
  const mouseMovedRef = useRef(false);
  const mouseDownRef = useRef(false);
  const overflowWrapperRef = useRef(null as any);

  const handleMouseDown = () => {
    mouseMovedRef.current = false;
    mouseDownRef.current = true;
  };
  const handleMouseMove = event => {
    if (mouseDownRef.current) {
      mouseMovedRef.current = true;
      // start scrolling horizontally
      overflowWrapperRef.current.scrollLeft =
        overflowWrapperRef.current.scrollLeft - event.movementX;
    }
  };
  const handleMouseUp = () => {
    mouseMovedRef.current = false;
    mouseDownRef.current = false;
  };
  const handleMouseLeave = () => {
    mouseMovedRef.current = false;
    mouseDownRef.current = false;
  };

  return {
    handleMouseLeave,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    mouseMovedRef,
    overflowWrapperRef,
  };
}
